/**
 * Plugin Loader
 *
 * Dynamically loads and registers UI plugins from workspace packages.
 * Plugins are loaded based on company configuration.
 */

/**
 * Plugin module interface
 */
type PluginModule = {
  register: () => void;
  unregister?: () => void;
  pluginId?: string;
};

/**
 * Loaded plugins map (pluginId -> module)
 */
const loadedPlugins = new Map<string, PluginModule>();

/**
 * Hard-disabled plugins: skipped even if a company has them in enabledPlugins.
 * Code stays in the workspace but is never registered, and the packages are
 * excluded from the build (their `build` scripts are no-ops). The matching
 * pluginLoaders entries are commented out so the bundler does not pull them in.
 */
const DISABLED_PLUGINS = new Set<string>(['rfq', 'finance']);

/**
 * Plugin loaders - direct imports from workspace packages
 */
const pluginLoaders: Record<string, () => Promise<PluginModule>> = {
  // Core plugins (always available to certain roles)
  admin: () => import('@lognai/plugin-admin-ui/register'),

  // Company plugins (loaded based on company configuration)
  products: () => import('@lognai/plugin-products-ui/register'),
  warehouse: () => import('@lognai/plugin-warehouse-ui/register'),
  orders: () => import('@lognai/plugin-orders-ui/register'),
  shipments: () => import('@lognai/plugin-shipments-ui/register'),
  'vendor-onboarding': () => import('@lognai/plugin-vendor-onboarding-ui/register'),
  inventory: () => import('@lognai/plugin-inventory-ui/register'),
  'port-lookup': () => import('@lognai/plugin-port-lookup-ui/register'),
  analytics: () => import('@lognai/plugin-analytics-ui/register'),
  'ai-core': () => import('@lognai/plugin-ai-core-ui/register'),
  procurement: () => import('@lognai/plugin-procurement-ui/register'),
  'landed-cost': () => import('@lognai/plugin-landed-cost-ui/register'),
  'global-insights': () => import('@lognai/plugin-global-insights-ui/register'),
  tasks: () => import('@lognai/plugin-tasks-ui/register'),
  tracking: () => import('@lognai/plugin-tracking-ui/register'),
  'shipment-planner': () => import('@lognai/plugin-shipment-planner-ui/register'),
  'freight-audit': () => import('@lognai/plugin-freight-audit-ui/register'),
  // Disabled plugins (see DISABLED_PLUGINS) — kept commented so the bundler
  // does not resolve their workspace packages:
  // rfq: () => import('@lognai/plugin-rfq-ui/register'),
  // finance: () => import('@lognai/plugin-finance-ui/register'),
};

/**
 * Core plugins that are loaded based on role, not company config
 * Empty array means all authenticated users get this plugin
 */
const CORE_PLUGINS: Record<string, string[]> = {
  admin: [], // All authenticated users (company features merged into admin)
  tasks: [], // Task management available to all authenticated users
  // The tracking board reads shipments, and its route requires
  // `shipment:shipment:read` — so a company without shipments never sees it, and it
  // does not need a separate company-level toggle.
  tracking: [],
  // Presentation-only planning dashboard (static mock, no backend / permissions),
  // surfaced as a flat "Shipment Planner" nav item for all internal users.
  'shipment-planner': [],
  // Presentation-only freight-audit mock (static quote/invoice comparison, no
  // backend / permissions), surfaced as a flat "Freight Audit" nav item.
  'freight-audit': [],
};

/**
 * Determine which core plugins to load based on user roles
 *
 * @param userRoles - Array of user's roles
 */
function getCorePluginsForRoles(userRoles: string[]): string[] {
  const plugins: string[] = [];

  for (const [pluginId, requiredRoles] of Object.entries(CORE_PLUGINS)) {
    // Empty array means all authenticated users get this plugin
    if (requiredRoles.length === 0 || requiredRoles.some((role) => userRoles.includes(role))) {
      plugins.push(pluginId);
    }
  }

  return plugins;
}

/**
 * Load and register plugins based on enabled list
 *
 * @param enabledPlugins - Array of plugin IDs to load
 */
export async function loadPlugins(enabledPlugins: string[]): Promise<void> {
  const skipped = enabledPlugins.filter((id) => DISABLED_PLUGINS.has(id));
  if (skipped.length > 0) {
    console.warn('[PluginLoader] Skipping hard-disabled plugins:', skipped);
  }
  const pluginsToLoad = enabledPlugins.filter((id) => !DISABLED_PLUGINS.has(id));

  console.log('[PluginLoader] Loading plugins:', pluginsToLoad);

  const loadPromises = pluginsToLoad.map(async (pluginId) => {
    if (loadedPlugins.has(pluginId)) {
      console.log(`[PluginLoader] Plugin already loaded: ${pluginId}`);
      return;
    }

    const loader = pluginLoaders[pluginId];
    if (!loader) {
      console.warn(`[PluginLoader] Unknown plugin: ${pluginId}`);
      return;
    }

    try {
      const plugin = await loader();
      plugin.register();
      loadedPlugins.set(pluginId, plugin);
      console.log(`[PluginLoader] Loaded plugin: ${pluginId}`);
    } catch (error) {
      console.error(`[PluginLoader] Failed to load plugin: ${pluginId}`, error);
    }
  });

  await Promise.all(loadPromises);
  console.log('[PluginLoader] All plugins loaded');
}

/**
 * Load plugins for a user based on their roles and company configuration
 *
 * @param userRoles - User's roles
 * @param companyEnabledPlugins - Plugins enabled for user's company
 */
export async function loadPluginsForUser(
  userRoles: string[],
  companyEnabledPlugins: string[],
  options: { isExternalPartner?: boolean } = {}
): Promise<void> {
  // External partner/supplier users get a minimal portal: only their company's
  // enabled plugins (e.g. vendor-onboarding), not internal core plugins (admin).
  const corePlugins = options.isExternalPartner ? [] : getCorePluginsForRoles(userRoles);

  // Combine core plugins with company-enabled plugins
  const allPlugins = [...new Set([...corePlugins, ...companyEnabledPlugins])];

  console.log('[PluginLoader] Loading plugins for user:', {
    roles: userRoles,
    corePlugins,
    companyPlugins: companyEnabledPlugins,
    allPlugins,
  });

  await loadPlugins(allPlugins);
}

/**
 * Unload a specific plugin
 *
 * @param pluginId - Plugin ID to unload
 */
export function unloadPlugin(pluginId: string): void {
  const plugin = loadedPlugins.get(pluginId);
  if (plugin) {
    if (plugin.unregister) {
      plugin.unregister();
    }
    loadedPlugins.delete(pluginId);
    console.log(`[PluginLoader] Unloaded plugin: ${pluginId}`);
  }
}

/**
 * Unload all plugins
 */
export function unloadAllPlugins(): void {
  for (const [pluginId, plugin] of loadedPlugins) {
    if (plugin.unregister) {
      plugin.unregister();
    }
    console.log(`[PluginLoader] Unloaded plugin: ${pluginId}`);
  }
  loadedPlugins.clear();
}

/**
 * Get list of loaded plugin IDs
 */
export function getLoadedPlugins(): string[] {
  return Array.from(loadedPlugins.keys());
}

/**
 * Check if a plugin is loaded
 */
export function isPluginLoaded(pluginId: string): boolean {
  return loadedPlugins.has(pluginId);
}

/**
 * Get all available plugin IDs
 */
export function getAvailablePlugins(): string[] {
  return Object.keys(pluginLoaders);
}
